import React, { useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import useAccess from "../../hooks/useAccess";
import "../../styles/TagEditorPage.css";
import "../../styles/TagCategories.css";

const TagItem = ({ tag, disabled }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "TAG",
    item: { t_id: tag.t_id },
    canDrag: !disabled, // 🧩 this disables dragging
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  return (
    <div
      ref={disabled ? null : drag}
      className={`tag-pill draggable ${disabled ? "disabled" : ""}`}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "move",
      }}
    >
      {tag.t_name}
    </div>
  );
};

const CategoryBox = ({
  children,
  isPending,
  name,
  onEdit,
  onDelete,
  onDropTag,
  disabled,
}) => {
  const [{ isOver }, drop] = useDrop({
    accept: "TAG",
    canDrop: () => !disabled, // 🧩 prevents dropping when disabled
    drop: (item) => !disabled && onDropTag(item.t_id),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  return (
    <div
      ref={disabled ? null : drop}
      className={`category-box ${isOver ? "highlight" : ""}`}
      style={{ opacity: disabled ? 0.6 : 1 }}
    >
      <div className="category-header">
        <strong className="category-title">{name}</strong>
        {isPending && (
          <div className="action-buttons">
            <button className="icon-btn" onClick={onEdit} title="Edit">
              <i className="fas fa-edit" />
            </button>
            <button
              className="icon-btn danger"
              onClick={onDelete}
              title="Delete"
            >
              <i className="fas fa-trash" />
            </button>
          </div>
        )}
      </div>
      <div className="category-content">{children}</div>
    </div>
  );
};

const EditTagsCategories = ({
  allTags,
  categoryList,
  tagCategories,
  setTagCategories,
  setCategoryList,
}) => {
  const [newCategoryName, setNewCategoryName] = useState("");
  const [pendingCategories, setPendingCategories] = useState([]);
  const [editingCatNames, setEditingCatNames] = useState({});

  const { hasPermission } = useAccess();

  const canEditTags = hasPermission("AccountSetting", "Tags", "edit");

  const handleDrop = (tagId, categoryId) => {
    if (!canEditTags) return;
    setTagCategories((prev) => ({
      ...prev,
      [tagId]: categoryId,
    }));
  };

  const handleCreateCategory = () => {
    if (!newCategoryName.trim()) return;
    const tempId = `new-${Date.now()}`;
    const newCat = { tc_id: tempId, tc_name: newCategoryName.trim() };

    setCategoryList([...categoryList, newCat]);
    setPendingCategories([...pendingCategories, newCat]);
    setNewCategoryName("");
  };

  const handleDeletePendingCategory = (tempId) => {
    setCategoryList((prev) => prev.filter((c) => c.tc_id !== tempId));
    setPendingCategories((prev) => prev.filter((c) => c.tc_id !== tempId));
    setTagCategories((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((tagId) => {
        if (updated[tagId] === tempId) updated[tagId] = "";
      });
      return updated;
    });
  };

  return (
    <div>
      {hasPermission("AccountSetting", "Tags", "edit") && (
        <>
          <label>
            <strong>Add New Category</strong>
          </label>
          <div className="add-tag-input-group">
            <input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Enter A New Tag Category Name Here"
            />
            <button onClick={handleCreateCategory} className="btn btn-light">
              Add
            </button>
          </div>
        </>
      )}

      <h4 className="mt-3">Existing Categories</h4>
      <div className="category-wrapper">
        <div className="category-grid">
          {/* Uncategorized Box */}
          <CategoryBox
            name="Uncategorized"
            onDropTag={(id) => handleDrop(id, "")}
            disabled={!canEditTags}
          >
            {allTags
              .filter((tag) => !tagCategories[tag.t_id])
              .map((tag) => (
                <TagItem key={tag.t_id} tag={tag} disabled={!canEditTags} />
              ))}
          </CategoryBox>

          {/* Dynamic Categories */}
          {categoryList.map((cat) => {
            const isPending = pendingCategories.some(
              (c) => c.tc_id === cat.tc_id
            );
            const tagsInCategory = allTags.filter(
              (tag) => tagCategories[tag.t_id] === String(cat.tc_id)
            );

            const isEditing = editingCatNames[cat.tc_id] !== undefined;

            return (
              <CategoryBox
                key={cat.tc_id}
                isPending={isPending}
                name={
                  isEditing ? (
                    <input
                      value={editingCatNames[cat.tc_id]}
                      onChange={(e) =>
                        setEditingCatNames({
                          ...editingCatNames,
                          [cat.tc_id]: e.target.value,
                        })
                      }
                      onBlur={() => {
                        const name = editingCatNames[cat.tc_id]?.trim();
                        if (name) {
                          const updatedList = categoryList.map((c) =>
                            c.tc_id === cat.tc_id ? { ...c, tc_name: name } : c
                          );
                          setCategoryList(updatedList);
                        }
                        const copy = { ...editingCatNames };
                        delete copy[cat.tc_id];
                        setEditingCatNames(copy);
                      }}
                      className="editable-cat-input"
                      autoFocus
                      disabled={!canEditTags} // 👈 Prevent editing if no permission
                    />
                  ) : (
                    cat.tc_name
                  )
                }
                onEdit={() =>
                  canEditTags && // 👈 Only allow if edit permission
                  setEditingCatNames({
                    ...editingCatNames,
                    [cat.tc_id]: cat.tc_name,
                  })
                }
                onDelete={
                  () => canEditTags && handleDeletePendingCategory(cat.tc_id) // 👈 Prevent delete
                }
                onDropTag={(id) => handleDrop(id, String(cat.tc_id))}
                disabled={!canEditTags} // 👈 Disable drag-and-drop for this category
              >
                {tagsInCategory.map((tag) => (
                  <TagItem key={tag.t_id} tag={tag} disabled={!canEditTags} /> // 👈 Disable drag
                ))}
              </CategoryBox>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EditTagsCategories;
